import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const faqs = [
    {
      question: 'How does SOLDAIR VPN protect my privacy?',
      answer: 'SOLDAIR VPN uses military-grade 256-bit encryption to secure your internet connection. We maintain a strict no-logs policy, ensuring your online activities remain completely private and anonymous.'
    },
    {
      question: 'Can I use SOLDAIR VPN on multiple devices?',
      answer: 'Yes! SOLDAIR VPN supports all major platforms including Windows, macOS, iOS, Android, and Linux. You can protect all your devices with a single account.'
    },
    {
      question: 'What is your uptime guarantee?',
      answer: 'We guarantee 99.9% uptime for our VPN service. Our global network of servers ensures reliable and consistent connectivity at all times.'
    },
    {
      question: 'Do you offer 24/7 customer support?',
      answer: 'Absolutely! Our dedicated support team is available 24/7 via email and phone to assist you with any questions or technical issues you may encounter.'
    },
    {
      question: 'How fast is SOLDAIR VPN?',
      answer: 'SOLDAIR VPN is optimized for maximum speed without compromising security. Our high-performance servers ensure minimal impact on your internet speed while keeping you protected.'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic will be added here
    console.log('Form submitted:', formData);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/62d71223-26d1-40a2-9b1b-fc55c1524fdd.png" 
                alt="SOLDAIR S.R.L. Logo" 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-semibold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>SOLDAIR S.R.L.</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Home</button>
              <button onClick={() => scrollToSection('vpn-service')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>VPN Service</button>
              <button onClick={() => scrollToSection('about')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>About Us</button>
              <button onClick={() => scrollToSection('location')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Location</button>
              <button onClick={() => scrollToSection('support')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Support</button>
              <button onClick={() => scrollToSection('contact')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'}`}>Contact</button>
              <button onClick={() => scrollToSection('vpn-service')} className="bg-cyan-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-cyan-600 transition-colors whitespace-nowrap cursor-pointer">
                Get Started <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden ${isScrolled ? 'text-gray-900' : 'text-white'} cursor-pointer`}
            >
              <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
              <div className="flex flex-col space-y-3 px-4 py-3">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-cyan-500 text-left cursor-pointer">Home</button>
                <button onClick={() => scrollToSection('vpn-service')} className="text-gray-700 hover:text-cyan-500 text-left cursor-pointer">VPN Service</button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-cyan-500 text-left cursor-pointer">About Us</button>
                <button onClick={() => scrollToSection('location')} className="text-gray-700 hover:text-cyan-500 text-left cursor-pointer">Location</button>
                <button onClick={() => scrollToSection('support')} className="text-gray-700 hover:text-cyan-500 text-left cursor-pointer">Support</button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-cyan-500 text-left cursor-pointer">Contact</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/47f07b8d16b6c4e0475ade1684686b2b.jpg" 
            alt="Secure VPN Network" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 border-2 border-white/30 rounded-full">
              <span className="text-white text-sm font-medium">Enterprise Security Solution</span>
            </div>
            <h1 className="text-7xl font-bold text-white mb-6 leading-tight">
              Secure Your Digital<br />
              <span className="italic">Freedom Today</span>
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-2xl">
              Experience unparalleled online privacy and security with SOLDAIR VPN. Military-grade encryption, lightning-fast speeds, and 24/7 protection for your digital life.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => scrollToSection('vpn-service')} className="bg-cyan-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-cyan-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                Explore VPN Service <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button onClick={() => scrollToSection('contact')} className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer">
                Contact Us
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-cyan-500">SOLDAIR</span> <span className="text-cyan-500">VPN</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced security features designed to protect your online presence and ensure complete privacy
            </p>
          </div>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {/* Large Feature Card */}
            <div className="lg:row-span-2 relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer h-[480px]">
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20encrypted%20data%20visualization%20with%20glowing%20teal%20and%20cyan%20digital%20locks%20and%20security%20shields%20on%20dark%20background%2C%20cybersecurity%20concept%20art%20with%20flowing%20data%20streams%20and%20encryption%20symbols%2C%20modern%20tech%20illustration&width=600&height=800&seq=feature-001&orientation=portrait" 
                alt="Military-Grade Encryption" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/90 via-teal-900/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="bg-black/70 backdrop-blur-sm rounded-2xl p-6">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-shield-check-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-white">Military-Grade Encryption</h3>
                  </div>
                  <p className="text-white/90 text-base">
                    256-bit AES encryption protects your data from hackers, ISPs, and government surveillance. Your online activities remain completely private and secure.
                  </p>
                </div>
              </div>
            </div>

            {/* Top Right Card */}
            <div className="relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer bg-emerald-50 h-[230px]">
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%20server%20network%20illustration%20with%20clean%20geometric%20shapes%20on%20soft%20mint%20green%20background%2C%20simple%20modern%20tech%20icon%20design%20with%20connected%20nodes%20and%20data%20centers%2C%20flat%20design%20style&width=600&height=300&seq=feature-002&orientation=landscape" 
                  alt="Global Server Network" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-global-line text-white text-xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">50+ Global Servers</h3>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Connect to our worldwide network for optimal speed and reliability
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Right Card */}
            <div className="relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer bg-pink-50 h-[230px]">
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=speed%20optimization%20graphics%20with%20dynamic%20motion%20lines%20and%20fast%20data%20transfer%20visualization%20on%20soft%20pink%20background%2C%20modern%20tech%20illustration%20showing%20high%20performance%20and%20velocity%2C%20clean%20minimal%20design&width=600&height=300&seq=feature-003&orientation=landscape" 
                  alt="Lightning Fast Speed" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-speed-line text-white text-xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Lightning Fast Speed</h3>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Optimized servers ensure maximum speed without compromising security
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-eye-off-line text-cyan-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">No-Logs Policy</h3>
              <p className="text-gray-600">
                We never track, collect, or share your private data. Your online activity is your business alone.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-device-line text-cyan-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Platform Support</h3>
              <p className="text-gray-600">
                Compatible with Windows, macOS, iOS, Android, and Linux. Protect all your devices seamlessly.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-time-line text-cyan-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">99.9% Uptime</h3>
              <p className="text-gray-600">
                Reliable and consistent connectivity guaranteed. Stay protected around the clock without interruption.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <p className="text-sm font-semibold text-gray-500 mb-4 tracking-wider uppercase">About SOLDAIR S.R.L.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-5xl font-bold text-gray-900 mb-6">
                  Protecting Your Digital Freedom Since Day One
                </h2>
              </div>
              <div>
                <p className="text-2xl text-gray-700 leading-relaxed">
                  SOLDAIR S.R.L. is dedicated to providing cutting-edge VPN technology that ensures your online privacy and security. Based in Argentina, we serve users worldwide with our commitment to digital freedom and data protection.
                </p>
              </div>
            </div>
          </div>

          {/* Statistics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 mt-20">
            <div className="text-center">
              <div className="text-6xl font-bold bg-gradient-to-r from-blue-900 to-cyan-500 bg-clip-text text-transparent mb-3">
                50+
              </div>
              <p className="text-lg text-gray-600">Global Servers</p>
            </div>

            <div className="text-center">
              <div className="text-6xl font-bold bg-gradient-to-r from-blue-900 to-cyan-500 bg-clip-text text-transparent mb-3">
                256-bit
              </div>
              <p className="text-lg text-gray-600">Encryption</p>
            </div>

            <div className="text-center">
              <div className="text-6xl font-bold bg-gradient-to-r from-blue-900 to-cyan-500 bg-clip-text text-transparent mb-3">
                24/7
              </div>
              <p className="text-lg text-gray-600">Support Available</p>
            </div>

            <div className="text-center">
              <div className="text-6xl font-bold bg-gradient-to-r from-blue-900 to-cyan-500 bg-clip-text text-transparent mb-3">
                99.9%
              </div>
              <p className="text-lg text-gray-600">Uptime Guarantee</p>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="mt-20 bg-white rounded-3xl p-12 shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h3>
                <p className="text-lg text-gray-700 mb-4">
                  At SOLDAIR S.R.L., we believe that online privacy is a fundamental right. Our mission is to make advanced VPN technology accessible to everyone, ensuring that your digital activities remain private and secure.
                </p>
                <p className="text-lg text-gray-700">
                  We continuously innovate and improve our services to stay ahead of emerging threats, providing you with the most reliable and secure VPN solution available.
                </p>
              </div>
              <div className="relative h-80 rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20team%20working%20on%20cybersecurity%20technology%20in%20modern%20office%20with%20digital%20screens%20showing%20network%20security%20data%20on%20simple%20light%20background%2C%20business%20technology%20concept%20with%20clean%20aesthetic%20and%20professional%20atmosphere&width=700&height=500&seq=about-001&orientation=landscape" 
                  alt="SOLDAIR Team" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Location</h2>
            <p className="text-xl text-gray-600">Visit us or reach out from anywhere in the world</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-5">
                {/* Address Information */}
                <div className="md:col-span-2 bg-gray-900 p-10 text-white">
                  <h3 className="text-2xl font-bold mb-8">Contact Information</h3>
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Address</p>
                      <p className="text-lg font-medium">AVELLANEDA Y ARTIGAS S/N</p>
                      <p className="text-lg font-medium">Choele Choel, R8360DSC</p>
                      <p className="text-lg font-medium">Argentina</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Email</p>
                      <a href="mailto:contact@soldairsrl.com" className="text-lg font-medium text-cyan-400 hover:text-cyan-300 cursor-pointer">
                        contact@soldairsrl.com
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Phone</p>
                      <a href="tel:+12603682595" className="text-lg font-medium text-cyan-400 hover:text-cyan-300 cursor-pointer">
                        +1 2603682595
                      </a>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="md:col-span-3 h-96 md:h-auto">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3213.8!2d-65.6667!3d-39.2833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDE2JzU5LjkiUyA2NcKwNDAnMDAuMSJX!5e0!3m2!1sen!2s!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="SOLDAIR S.R.L. Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-24 bg-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Get Support</h2>
            <p className="text-xl text-gray-600">We're here to help you 24/7</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* FAQ Section */}
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-2xl shadow-md overflow-hidden transition-all hover:shadow-lg"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
                    >
                      <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                      <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-cyan-500 flex-shrink-0`}></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-5 border-l-4 border-cyan-500">
                        <p className="text-gray-700">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-24">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-customer-service-2-line text-cyan-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Need Direct Help?</h3>
                <p className="text-gray-600 text-center mb-8">
                  Our support team is available 24/7 to assist you with any questions or issues.
                </p>
                <div className="space-y-4">
                  <a 
                    href="mailto:contact@soldairsrl.com"
                    className="block w-full bg-cyan-500 text-white px-6 py-4 rounded-xl font-semibold text-center hover:bg-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Email Support
                  </a>
                  <a 
                    href="tel:+12603682595"
                    className="block w-full bg-white text-gray-900 px-6 py-4 rounded-xl font-semibold text-center hover:bg-gray-50 transition-colors border-2 border-gray-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-xl text-gray-600">Get in touch with our team</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-xl p-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-base"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-base"
                      placeholder="john@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject
                    </label>
                    <select
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-base cursor-pointer"
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-base resize-none"
                      placeholder="Tell us how we can help you..."
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-cyan-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-cyan-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    Send Message <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Information Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gray-900 rounded-3xl shadow-xl p-8 text-white h-full">
                <h3 className="text-2xl font-bold mb-8">Contact Information</h3>
                
                <div className="space-y-8">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Email</p>
                      <a href="mailto:contact@soldairsrl.com" className="text-lg font-medium hover:text-cyan-400 transition-colors cursor-pointer">
                        contact@soldairsrl.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-phone-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Phone</p>
                      <a href="tel:+12603682595" className="text-lg font-medium hover:text-cyan-400 transition-colors cursor-pointer">
                        +1 2603682595
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Address</p>
                      <p className="text-lg font-medium">
                        AVELLANEDA Y ARTIGAS S/N<br />
                        Choele Choel, R8360DSC<br />
                        Argentina
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Support Hours</p>
                      <p className="text-lg font-medium">24/7 Available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 rounded-t-[32px] pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="md:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/62d71223-26d1-40a2-9b1b-fc55c1524fdd.png" 
                  alt="SOLDAIR S.R.L." 
                  className="h-10 w-auto"
                />
                <h3 className="text-2xl font-bold text-white">SOLDAIR S.R.L.</h3>
              </div>
              <p className="text-gray-400 text-base">
                Protecting your digital freedom with advanced VPN technology and uncompromising security.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-lg">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('vpn-service')} className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    VPN Service
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('location')} className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    Location
                  </button>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-lg">Support</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('support')} className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    Help Center
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    Contact
                  </button>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors text-base cursor-pointer">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-lg">Stay Connected</h4>
              <p className="text-gray-400 text-sm mb-4">
                Subscribe to our newsletter for updates and security tips
              </p>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                />
                <button className="bg-cyan-500 text-white w-10 h-10 rounded-r-lg hover:bg-cyan-600 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2025 SOLDAIR S.R.L. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="nofollow noopener noreferrer" 
                className="text-gray-400 hover:text-cyan-400 transition-colors text-sm cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>

          {/* Watermark */}
          <div className="mt-8 text-center">
            <p className="text-gray-800 text-6xl font-bold opacity-50">SOLDAIR</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
